// src/components/common/MobileNavigation.jsx
import React, { useState, useEffect } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Badge,
    Fab,
    Box,
    useMediaQuery,
    useTheme,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Home as HomeIcon,
    Dashboard as DashboardIcon,
    TrendingUp as TrendingIcon,
    MenuBook as DocsIcon,
    Menu as MenuIcon,
    Science as ResearchIcon,
    Compare as CompareIcon,
    Favorite as SponsorIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

// Main mobile navigation component
const MobileNavigation = ({ onMenuToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { state } = useAppContext();
    const [value, setValue] = useState(0);

    // Navigation items for bottom bar
    const navItems = [
        { 
            label: 'Home', 
            path: '/', 
            icon: <HomeIcon sx={{ fontSize: '1.5rem' }} />,
            badge: null
        },
        { 
            label: 'Analysis', 
            path: '/analysis', 
            icon: <DashboardIcon sx={{ fontSize: '1.5rem' }} />,
            badge: null
        },
        { 
            label: 'Results', 
            path: '/results', 
            icon: <TrendingIcon sx={{ fontSize: '1.5rem' }} />,
            badge: state.recentAnalyses?.length || null
        },
        { 
            label: 'Docs', 
            path: '/documentation', 
            icon: <DocsIcon sx={{ fontSize: '1.5rem' }} />,
            badge: null
        },
    ];

    // Update selected value based on current path
    useEffect(() => {
        const currentIndex = navItems.findIndex(item => {
            if (item.path === '/') {
                return location.pathname === '/';
            }
            return location.pathname.startsWith(item.path);
        });
        setValue(currentIndex >= 0 ? currentIndex : 0);
    }, [location.pathname]);

    // Handle navigation change
    const handleNavigation = (event, newValue) => {
        setValue(newValue);
        if (navItems[newValue]) {
            navigate(navItems[newValue].path);
        }
    };

    // Don't render on desktop
    if (!isMobile) return null;

    return (
        <>
            {/* Bottom Navigation Bar */}
            <Paper 
                sx={{ 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    zIndex: theme.zIndex.bottomNavigation,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }} 
                elevation={8}
            >
                <BottomNavigation
                    value={value}
                    onChange={handleNavigation}
                    showLabels
                    sx={{
                        height: 70,
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: 'auto',
                            paddingTop: 1,
                        },
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: '0.75rem',
                            '&.Mui-selected': {
                                fontSize: '0.75rem',
                            },
                        },
                    }}
                >
                    {navItems.map((item, index) => (
                        <BottomNavigationAction
                            key={item.path}
                            label={item.label}
                            icon={
                                item.badge ? (
                                    <Badge 
                                        badgeContent={item.badge} 
                                        color="primary"
                                        max={99}
                                    >
                                        {item.icon}
                                    </Badge>
                                ) : (
                                    item.icon
                                )
                            }
                        />
                    ))}
                </BottomNavigation>
            </Paper>

            {/* Floating Menu Button - Hidden on mobile to prevent footer overlap */}
            {/* Drawer menu is accessible via sidebar on mobile */}

            {/* Bottom spacing for content */}
            <Box sx={{ height: 70 }} />
        </>
    );
};

// Quick access FAB for common actions
export const QuickActionFAB = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [visible, setVisible] = useState(false);

    // Show/hide based on scroll and page
    useEffect(() => {
        let scrollTimeout;
        
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            setVisible(false);
            
            scrollTimeout = setTimeout(() => {
                const shouldShow = window.scrollY > 200 && 
                                  !location.pathname.includes('/analysis') &&
                                  !location.pathname.includes('/book-demo');
                setVisible(shouldShow);
            }, 150);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        
        // Initial check
        const shouldShow = window.scrollY > 200 && 
                          !location.pathname.includes('/analysis') &&
                          !location.pathname.includes('/book-demo');
        setVisible(shouldShow);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [location.pathname]);

    // Don't show on desktop or if not visible
    if (!isMobile || !visible) return null;

    return (
        <Fab
            color="secondary"
            size="small"
            onClick={() => navigate('/analysis')}
            sx={{
                position: 'fixed',
                bottom: 160, // Above menu FAB and bottom nav
                right: 16,
                zIndex: theme.zIndex.fab - 1,
                transition: 'all 0.3s ease',
                transform: visible ? 'scale(1)' : 'scale(0)',
                width: 48,
                height: 48,
            }}
        >
            <DashboardIcon sx={{ fontSize: '1.25rem' }} />
        </Fab>
    );
};

// Enhanced mobile drawer content
export const MobileDrawerContent = ({ onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = useAppContext();

    const handleNavigation = (path) => {
        navigate(path);
        onClose();
    };

    // All navigation items for drawer
    const allNavItems = [
        { label: 'Home', path: '/', icon: <HomeIcon /> },
        { label: 'Analysis', path: '/analysis', icon: <DashboardIcon /> },
        { label: 'Research', path: '/research', icon: <ResearchIcon /> },
        { label: 'Comparison', path: '/comparison', icon: <CompareIcon /> },
        { label: 'Documentation', path: '/documentation', icon: <DocsIcon /> },
        { label: 'Sponsorship', path: '/sponsorship', icon: <SponsorIcon /> },
    ];

    return (
        <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer header - reuse existing header from Header.jsx */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1rem' }}>AP</Typography>
                    </Box>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                        AutoPostureCV
                    </Typography>
                </Box>
            </Box>

            {/* Quick stats or user info */}
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                    Quick Access
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label="New Analysis"
                        size="small"
                        color="primary"
                        clickable
                        onClick={() => handleNavigation('/analysis')}
                    />
                    <Chip
                        label="Book Demo"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        clickable
                        onClick={() => handleNavigation('/book-demo')}
                    />
                </Box>
            </Box>

            {/* Navigation items */}
            <List sx={{ flex: 1, py: 1 }}>
                {allNavItems.map((item) => {
                    const active = location.pathname === item.path || 
                                  (item.path !== '/' && location.pathname.startsWith(item.path));
                    return (
                        <ListItem key={item.label} disablePadding>
                            <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                    mx: 1, borderRadius: 2, mb: 0.5,
                                    bgcolor: active ? 'primary.main' : 'transparent',
                                    '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
                                }}
                            >
                                <ListItemIcon sx={{ color: active ? 'white' : 'text.secondary', minWidth: 40 }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: active ? 700 : 400,
                                        color: active ? 'white' : 'text.primary',
                                        fontSize: '0.95rem',
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

export default MobileNavigation;